import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import {
  getStaffDashboard, getPlandayShifts, acknowledgeAnnouncement, getPersonalTasks,
  getStaffHomeV2, publishStaffReadinessSnapshot,
  GetStaffDashboardOutputType, GetPlandayShiftsOutputType, GetPersonalTasksOutputType,
  GetStaffHomeV2OutputType,
} from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import SkillBadges from '@/components/dashboard/SkillBadges';
import QuickActions from '@/components/dashboard/QuickActions';
import TrainingProgressSection from '@/components/dashboard/TrainingProgress';
import EmployeeOfMonthBanner from '@/components/dashboard/EmployeeOfMonthBanner';
import WelcomePanel from '@/components/dashboard/WelcomePanel';
import { format, parseISO } from 'date-fns';
import {
  Cloud, CloudSun, Sun, CloudRain, CloudSnow, Megaphone, Clock, Calendar,
  CheckSquare, ExternalLink, Loader2, Flame, AlertTriangle, RefreshCw,
  ShieldAlert, FileEdit, BookOpen, Zap, ChevronRight, WifiOff, Trophy,
  CalendarDays, Star, FileText,
} from 'lucide-react';
import { getCached, setCached, formatCacheAge, isCacheStale } from '../lib/localCache';
import { Link } from 'react-router-dom';
import { useFeatureToggles } from '../components/FeatureToggleProvider';

type DashData = GetStaffDashboardOutputType;
type Action = DashData['actions'][0];
type Announcement = DashData['announcements'][0];
type Shift = GetPlandayShiftsOutputType['shifts'][0];

const WEATHER_CODES: Record<number, { desc: string; Icon: React.FC<{className?: string}> }> = {
  0: { desc: 'Clear sky', Icon: Sun }, 1: { desc: 'Mainly clear', Icon: CloudSun },
  2: { desc: 'Partly cloudy', Icon: CloudSun }, 3: { desc: 'Overcast', Icon: Cloud },
  51: { desc: 'Light drizzle', Icon: CloudRain }, 61: { desc: 'Rain', Icon: CloudRain },
  71: { desc: 'Snow', Icon: CloudSnow }, 80: { desc: 'Rain showers', Icon: CloudRain },
  95: { desc: 'Thunderstorm', Icon: CloudRain },
};
function getWeatherInfo(code: number) {
  const base = WEATHER_CODES[code];
  if (base) return base;
  if (code >= 51 && code <= 67) return { desc: 'Rain', Icon: CloudRain };
  if (code >= 71 && code <= 77) return { desc: 'Snow', Icon: CloudSnow };
  return { desc: 'Cloudy', Icon: Cloud };
}

const HUB_URL = 'https://app.zite.so/preview/29oocs3fdn';

const urgencyConfig = {
  overdue: { color: 'bg-destructive/10 border-destructive/30 text-destructive', icon: AlertTriangle, label: 'Overdue' },
  today: { color: 'bg-primary/10 border-primary/30 text-primary', icon: Clock, label: 'Today' },
  due_soon: { color: 'bg-accent/20 border-accent/40 text-accent-foreground', icon: Calendar, label: 'Due Soon' },
  waiting: { color: 'bg-muted border-border text-muted-foreground', icon: Clock, label: 'Waiting' },
  info: { color: 'bg-muted border-border text-muted-foreground', icon: CheckSquare, label: 'Info' },
};

function ActionCard({ action }: { action: Action }) {
  const config = urgencyConfig[action.urgency];
  const Icon = config.icon;
  return (
    <Link to={action.link} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:opacity-80 no-underline ${config.color}`}>
      <Icon className="w-4 h-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{action.title}</p>
        {action.subtitle && <p className="text-xs text-muted-foreground truncate">{action.subtitle}</p>}
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
    </Link>
  );
}

function ShiftsCard({ shifts, failed, isLinked }: { shifts: Shift[]; failed: boolean; isLinked: boolean }) {
  if (!isLinked) return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> My Shifts</h2>
      <div className="flex items-center gap-2 text-muted-foreground"><WifiOff className="w-4 h-4" /><p className="text-xs italic">Personal profile not linked — shifts unavailable</p></div>
    </div>
  );
  if (failed) return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> My Shifts</h2>
      <div className="flex items-center gap-2 text-muted-foreground"><WifiOff className="w-4 h-4" /><p className="text-xs italic">PlanDay roster not connected</p></div>
    </div>
  );
  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> My Upcoming Shifts</h2>
      {shifts.length === 0 ? <p className="text-xs text-muted-foreground italic">No upcoming shifts scheduled.</p> : (
        <div className="space-y-2">
          {shifts.map(s => (
            <div key={s.id} className="flex items-center gap-2.5">
              <div className="w-10 text-center bg-muted rounded py-1 shrink-0">
                <p className="text-xs font-bold text-primary leading-none">{s.date ? format(parseISO(s.date), 'd') : '?'}</p>
                <p className="text-xs text-muted-foreground">{s.date ? format(parseISO(s.date), 'MMM') : ''}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-foreground">{s.date ? format(parseISO(s.date), 'EEEE') : 'Shift'}</p>
                <p className="text-xs text-muted-foreground">{[s.startTime && s.endTime ? `${s.startTime}–${s.endTime}` : '', s.departmentName].filter(Boolean).join(' · ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<DashData | null>(null);
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [shiftsFailed, setShiftsFailed] = useState(false);
  const [weather, setWeather] = useState<{ temp: number; desc: string; code: number } | null>(null);
  const [ackAnnouncement, setAckAnnouncement] = useState<Announcement | null>(null);
  const [ackInitials, setAckInitials] = useState('');
  const [ackChecked, setAckChecked] = useState(false);
  const [ackSaving, setAckSaving] = useState(false);
  const [cacheAge, setCacheAge] = useState<Date | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [personalTasks, setPersonalTasks] = useState<GetPersonalTasksOutputType | null>(null);
  const [v2Data, setV2Data] = useState<GetStaffHomeV2OutputType | null>(null);

  const loadData = useCallback(() => {
    if (!user) return;
    // Show cached data immediately — but invalidate if cache predates canonicalLink field
    const cached = getCached<{ data: DashData; shifts: Shift[] }>('dashboard_v2');
    if (cached && cached.data.data.canonicalLink) {
      setData(cached.data.data);
      setShifts(cached.data.shifts);
      setCacheAge(cached.savedAt);
      setIsStale(isCacheStale(cached.savedAt, 10));
      setLoading(false);
    }

    // Single consolidated call + separate Planday call (external API, may fail)
    const dashPromise = getStaffDashboard({}).then(d => { setData(d); return d; });
    const shiftsPromise = getPlandayShifts({})
      .then(d => { const sh = d.shifts.slice(0, 3); setShifts(sh); setShiftsFailed(false); return sh; })
      .catch(() => { setShiftsFailed(true); return [] as Shift[]; });

    Promise.all([dashPromise, shiftsPromise])
      .then(([dashData, sh]) => {
        setCached('dashboard_v2', { data: dashData, shifts: sh });
        setCacheAge(new Date());
        setIsStale(false);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    // Personal tasks (fire-and-forget)
    getPersonalTasks({}).then(d => setPersonalTasks(d)).catch(() => {});

    // Staff Home v2 extras (fire-and-forget)
    getStaffHomeV2({}).then(d => setV2Data(d)).catch(() => {});

    // Weather (external, fire-and-forget)
    fetch('https://api.open-meteo.com/v1/forecast?latitude=51.5808&longitude=-2.1576&current=temperature_2m,weather_code&timezone=Europe/London')
      .then(r => r.json())
      .then(d => setWeather({ temp: Math.round(d.current.temperature_2m), desc: getWeatherInfo(d.current.weather_code).desc, code: d.current.weather_code }))
      .catch(() => {});

    // BMS Staff Hub auto-sync on load for manager+ (fire-and-forget)
    const userRole = user.role;
    if (userRole === 'Manager' || userRole === 'Admin' || userRole === 'Owner' || userRole === 'Supervisor') {
      publishStaffReadinessSnapshot({}).catch(() => {});
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAck = async () => {
    if (!ackAnnouncement) return;
    if (ackAnnouncement.acknowledgementType === 'Type Initials' && !ackInitials.trim()) { toast.error('Please enter your initials'); return; }
    setAckSaving(true);
    try {
      await acknowledgeAnnouncement({ announcementId: ackAnnouncement.id, acknowledged: true, initials: ackInitials, existingReadId: ackAnnouncement.myReadId || undefined });
      toast.success('Acknowledged!');
      setData(prev => prev ? { ...prev, announcements: prev.announcements.filter(a => a.id !== ackAnnouncement.id) } : prev);
      setAckAnnouncement(null);
    } catch { toast.error('Failed to acknowledge'); }
    finally { setAckSaving(false); }
  };

  const { isRouteEnabled } = useFeatureToggles();
  // Strict linked check — if canonicalLink field is absent (stale cache / old endpoint), treat as unlinked
  const isLinked = data?.canonicalLink != null && data.canonicalLink.linked === true;
  const displayName = isLinked
    ? (data?.canonicalLink?.staffName || data?.user.preferredName || data?.user.firstName || '')
    : (data?.user.preferredName || data?.user.firstName || '');
  const subtitle = isLinked
    ? [data?.user.position, data?.user.department].filter(Boolean).join(' · ')
    : 'General staff session';
  const today = format(new Date(), 'EEEE d MMMM');
  const isManager = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Owner';
  // Actions — only populated in linked render path (unlinked returns early above)
  const allActions = data?.actions || [];
  const urgentActions = allActions.filter(a => a.urgency === 'overdue' || a.urgency === 'today');
  const soonActions = allActions.filter(a => a.urgency === 'due_soon');
  const waitingActions = allActions.filter(a => a.urgency === 'waiting');

  if (loading && !data) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-3"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // UNLINKED ACCOUNT — completely separate render path.
  // No personal actions, announcements, skills, onboarding, compliance,
  // training progress, shifts, fire-check, or sign-off claims.
  // ──────────────────────────────────────────────────────────────────────────
  if (data && !isLinked) {
    return (
      <div className="min-h-screen bg-background pb-4 md:pb-0">
        <div className="px-4 md:px-8 py-4 md:py-6 max-w-6xl mx-auto space-y-4">
          {/* Unlinked account banner */}
          <div className="flex items-center gap-2 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <span><strong>Unlinked account</strong> — your login ({user?.email}) is not matched to a BMS staff identity. Personal training, compliance and shift data is not available. <Link to="/team-roster" className="text-primary hover:underline">View team roster →</Link></span>
          </div>

          {/* Welcome header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground">Welcome back 👋</h1>
              <p className="text-sm text-muted-foreground mt-0.5">General staff session · {today}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <a href={HUB_URL} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 bg-card border border-border rounded-lg px-3 py-2 text-sm font-medium text-primary hover:bg-muted transition-colors no-underline">
                <ExternalLink className="w-3.5 h-3.5" /> Hub
              </a>
              {weather && (() => { const { Icon } = getWeatherInfo(weather.code); return (
                <div className="bg-card border border-border rounded-lg px-3 py-2 flex items-center gap-2">
                  <Icon className="w-4 h-4 text-primary" />
                  <div><p className="text-sm font-bold text-foreground leading-none">{weather.temp}°C</p><p className="text-xs text-muted-foreground">Sherston</p></div>
                </div>
              ); })()}
            </div>
          </div>

          {/* Canonical team summary — safe truth */}
          {data.canonicalSummary && (
            <div className="flex items-center gap-3 text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2">
              <span className="font-medium text-foreground">{data.canonicalSummary.activeStaffCount} active staff</span>
              <span>·</span>
              <span>{data.canonicalSummary.trainingOverdueCount} training overdue</span>
              <span>·</span>
              <span>{data.canonicalSummary.complianceAttentionCount} compliance attention</span>
              <span className="ml-auto text-[10px]">Source: BMS</span>
            </div>
          )}

          {/* Neutral navigation cards — no personal claims */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/document-library" className="bg-card border border-border rounded-lg p-3 no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Docs & Policies</span>
              </div>
              <p className="text-sm text-muted-foreground">View library</p>
            </Link>
            <Link to="/notifications" className="bg-card border border-border rounded-lg p-3 no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <Megaphone className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Messages</span>
              </div>
              <p className="text-sm text-muted-foreground">Open messages</p>
            </Link>
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Skills & Capabilities</span>
              </div>
              <p className="text-sm text-muted-foreground">Not assessed</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckSquare className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Onboarding</span>
              </div>
              <p className="text-sm text-muted-foreground">Status unavailable</p>
            </div>
          </div>

          {/* Training — Not assessed */}
          {isRouteEnabled('/modules') && (
            <div className="bg-card border border-border rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Training Progress</h2>
                <Link to="/training-videos" className="text-xs text-primary hover:underline">View library →</Link>
              </div>
              <p className="text-sm text-muted-foreground">Not assessed — personal training progress requires a linked BMS staff identity.</p>
              <p className="text-xs text-muted-foreground mt-1">{data.trainingStats.totalModules} modules available in the canonical catalogue.</p>
            </div>
          )}

          {/* Shifts — unavailable */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> My Shifts</h2>
            <div className="flex items-center gap-2 text-muted-foreground"><WifiOff className="w-4 h-4" /><p className="text-xs italic">Personal profile not linked — shifts unavailable</p></div>
          </div>

          {/* All caught up — no actions for unlinked */}
          <div className="bg-card border border-border rounded-lg p-6 text-center">
            <CheckSquare className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No verified actions for this session.</p>
          </div>
        </div>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // LINKED ACCOUNT — full personal dashboard
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background pb-4 md:pb-0">
      <div className="px-4 md:px-8 py-4 md:py-6 max-w-6xl mx-auto space-y-4">
        {/* Welcome header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground">Welcome back{displayName ? `, ${displayName}` : ''} 👋</h1>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <p className="text-sm text-muted-foreground">{subtitle ? `${subtitle} · ` : ''}{today}</p>
              {isStale && cacheAge && <span className="flex items-center gap-1 text-xs text-muted-foreground/60 italic"><RefreshCw className="w-3 h-3 animate-spin" /> refreshing…</span>}
              {!isStale && cacheAge && <span className="text-xs text-muted-foreground/50">· {formatCacheAge(cacheAge)}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href={HUB_URL} target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-1.5 bg-card border border-border rounded-lg px-3 py-2 text-sm font-medium text-primary hover:bg-muted transition-colors no-underline">
              <ExternalLink className="w-3.5 h-3.5" /> Hub
            </a>
            {weather && (() => { const { Icon } = getWeatherInfo(weather.code); return (
              <div className="bg-card border border-border rounded-lg px-3 py-2 flex items-center gap-2">
                <Icon className="w-4 h-4 text-primary" />
                <div><p className="text-sm font-bold text-foreground leading-none">{weather.temp}°C</p><p className="text-xs text-muted-foreground">Sherston</p></div>
              </div>
            ); })()}
          </div>
        </div>

        {/* Canonical team summary — safe truth */}
        {data?.canonicalSummary && (
          <div className="flex items-center gap-3 text-xs text-muted-foreground bg-muted/50 border border-border rounded-lg px-3 py-2">
            <span className="font-medium text-foreground">{data.canonicalSummary.activeStaffCount} active staff</span>
            <span>·</span>
            <span>{data.canonicalSummary.trainingOverdueCount} training overdue</span>
            <span>·</span>
            <span>{data.canonicalSummary.complianceAttentionCount} compliance attention</span>
            <span className="ml-auto text-[10px]">Source: BMS</span>
          </div>
        )}

        {/* Priority Stat Badges */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {data.complianceOverdue > 0 && isRouteEnabled('/compliance') && (
              <Link to="/compliance" className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 no-underline">
                <ShieldAlert className="w-4 h-4 text-destructive shrink-0" />
                <div><p className="text-lg font-bold text-foreground leading-none">{data.complianceOverdue}</p><p className="text-xs text-muted-foreground">Overdue</p></div>
              </Link>
            )}
            {data.complianceDueSoon > 0 && isRouteEnabled('/compliance') && (
              <Link to="/compliance" className="flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-lg px-3 py-2 no-underline">
                <ShieldAlert className="w-4 h-4 text-accent-foreground shrink-0" />
                <div><p className="text-lg font-bold text-foreground leading-none">{data.complianceDueSoon}</p><p className="text-xs text-muted-foreground">Due Soon</p></div>
              </Link>
            )}
            {data.overdueTraining > 0 && isRouteEnabled('/modules') && (
              <Link to="/modules" className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 no-underline">
                <BookOpen className="w-4 h-4 text-primary shrink-0" />
                <div><p className="text-lg font-bold text-foreground leading-none">{data.overdueTraining}</p><p className="text-xs text-muted-foreground">Training</p></div>
              </Link>
            )}
            {data.pendingEditRequests > 0 && (
              <Link to="/profile" className="flex items-center gap-2 bg-muted border border-border rounded-lg px-3 py-2 no-underline">
                <FileEdit className="w-4 h-4 text-muted-foreground shrink-0" />
                <div><p className="text-lg font-bold text-foreground leading-none">{data.pendingEditRequests}</p><p className="text-xs text-muted-foreground">Edits Pending</p></div>
              </Link>
            )}
            {(data.user.pointsBalance ?? 0) > 0 && isRouteEnabled('/my-rewards') && (
              <Link to="/my-rewards" className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2 no-underline">
                <Trophy className="w-4 h-4 text-primary shrink-0" />
                <div><p className="text-lg font-bold text-foreground leading-none flex items-center gap-1"><Zap className="w-3.5 h-3.5" />{data.user.pointsBalance}</p><p className="text-xs text-muted-foreground">Points</p></div>
              </Link>
            )}
          </div>
        )}

        {/* Manager alerts */}
        {isManager && data?.managerAlerts && (data.managerAlerts.overdueTaskCount > 0 || data.managerAlerts.fireOverdue || data.managerAlerts.pendingSignoffs > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {data.managerAlerts.overdueTaskCount > 0 && (
              <Link to="/tasks" className="flex items-center gap-3 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5 no-underline hover:bg-destructive/15 transition-colors">
                <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                <div><p className="text-sm font-semibold text-foreground">{data.managerAlerts.overdueTaskCount} task{data.managerAlerts.overdueTaskCount > 1 ? 's' : ''} overdue</p></div>
              </Link>
            )}
            {data.managerAlerts.fireOverdue && (
              <Link to="/fire-log" className="flex items-center gap-3 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2.5 no-underline hover:bg-destructive/15 transition-colors">
                <Flame className="w-4 h-4 text-destructive shrink-0" />
                <div><p className="text-sm font-semibold text-foreground">Fire check overdue</p></div>
              </Link>
            )}
            {data.managerAlerts.pendingSignoffs > 0 && (
              <Link to="/manager" className="flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2.5 no-underline hover:bg-primary/15 transition-colors">
                <CheckSquare className="w-4 h-4 text-primary shrink-0" />
                <div><p className="text-sm font-semibold text-foreground">{data.managerAlerts.pendingSignoffs} sign-off{data.managerAlerts.pendingSignoffs > 1 ? 's' : ''} pending</p></div>
              </Link>
            )}
          </div>
        )}

        {/* Unread announcements */}
        {data?.announcements.map(a => (
          <div key={a.id} className="bg-primary/10 border border-primary/30 rounded-lg px-3 py-2.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <Megaphone className="w-4 h-4 text-primary shrink-0" />
              <div className="min-w-0"><p className="text-sm font-semibold text-foreground truncate">{a.title}</p></div>
            </div>
            <Button size="sm" className="shrink-0 bg-primary text-primary-foreground text-xs h-7" onClick={() => { setAckAnnouncement(a); setAckInitials(''); setAckChecked(false); }}>
              {a.acknowledgementType !== 'None' ? 'Acknowledge' : 'Read'}
            </Button>
          </div>
        ))}

        {/* Welcome / Onboarding panel for new staff */}
        {data && (
          <WelcomePanel
            name={displayName}
            onboardingComplete={data.onboardingProgress.isSubmitted}
            onboardingSteps={data.onboardingProgress.stepsCompleted}
            onboardingTotal={data.onboardingProgress.totalSteps}
            profileComplete={Math.round(([
              data.user.firstName, data.user.position, data.user.department,
              data.user.startDate, data.user.employmentType,
              (data.user.skills?.length || 0) > 0 ? 'yes' : '',
            ].filter(Boolean).length / 6) * 100)}
            hasTraining={data.trainingStats.totalModules > 0}
          />
        )}

        {/* V2: Leave, Docs, Recognition, Onboarding Journey */}
        {v2Data && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Leave status */}
            {isRouteEnabled('/leave') && <Link to="/leave" className="bg-card border border-border rounded-lg p-3 no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <CalendarDays className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Leave</span>
              </div>
              {v2Data.leave.nextLeave ? (
                <p className="text-sm text-foreground font-medium truncate">{v2Data.leave.nextLeave.title}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No upcoming leave</p>
              )}
              <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                {v2Data.leave.pending > 0 && <span>{v2Data.leave.pending} pending</span>}
                {v2Data.leave.holidayBalance != null && <span>{v2Data.leave.holidayBalance}/{v2Data.leave.holidayAllowance || '—'} days</span>}
              </div>
            </Link>}

            {/* Docs/Policies awaiting ack */}
            <Link to="/document-library" className="bg-card border border-border rounded-lg p-3 no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Docs & Policies</span>
              </div>
              {v2Data.policiesAwaitingAck.length > 0 ? (
                <p className="text-sm text-foreground font-medium">{v2Data.policiesAwaitingAck.length} polic{v2Data.policiesAwaitingAck.length > 1 ? 'ies' : 'y'} to acknowledge</p>
              ) : (
                <p className="text-sm text-muted-foreground">All policies acknowledged ✓</p>
              )}
            </Link>

            {/* Recognition */}
            {isRouteEnabled('/recognition') && <Link to="/recognition" className="bg-card border border-border rounded-lg p-3 no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <Star className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Recognition</span>
              </div>
              <p className="text-sm text-foreground font-medium">{v2Data.recognition.received} received · {v2Data.recognition.given} given</p>
              {v2Data.recognition.recentTitle && <p className="text-xs text-muted-foreground mt-0.5 truncate">Latest: {v2Data.recognition.recentTitle}</p>}
            </Link>}

            {/* Messages */}
            <Link to="/notifications" className="bg-card border border-border rounded-lg p-3 no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <Megaphone className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Messages</span>
              </div>
              {v2Data.unreadMessages > 0 ? (
                <p className="text-sm text-foreground font-medium">{v2Data.unreadMessages} unread message{v2Data.unreadMessages > 1 ? 's' : ''}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No unread messages</p>
              )}
            </Link>
          </div>
        )}

        {/* V2: Onboarding journey progress */}
        {v2Data?.onboardingJourney.active && (
          <Link to="/onboarding-journeys" className="block bg-primary/5 border border-primary/20 rounded-lg p-4 no-underline hover:bg-primary/10 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-foreground">📋 {v2Data.onboardingJourney.journeyName || 'Onboarding Journey'}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{v2Data.onboardingJourney.status} · {v2Data.onboardingJourney.progress}% complete</p>
              </div>
              <div className="w-20 bg-muted rounded-full h-2">
                <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${v2Data.onboardingJourney.progress}%` }} />
              </div>
            </div>
          </Link>
        )}

        {/* Personal tasks summary */}
        {personalTasks && (personalTasks.counts.todo + personalTasks.counts.inProgress) > 0 && (
          <Link to="/my-tasks" className="block bg-card border border-border rounded-lg p-4 no-underline hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-primary" /> My Tasks
              </h2>
              <span className="text-xs text-primary font-medium">{personalTasks.counts.todo + personalTasks.counts.inProgress} active →</span>
            </div>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span>{personalTasks.counts.todo} to do</span>
              <span>{personalTasks.counts.inProgress} in progress</span>
              <span>{personalTasks.counts.done} done</span>
            </div>
          </Link>
        )}

        {/* Action Queue + Shifts + Events */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Action queue — priority column */}
          <div className="md:col-span-2 space-y-3">
            {urgentActions.length > 0 && (
              <div className="space-y-1.5">
                <h3 className="text-xs font-semibold text-destructive uppercase tracking-wider">Needs Attention</h3>
                {urgentActions.map(a => <ActionCard key={a.id} action={a} />)}
              </div>
            )}
            {soonActions.length > 0 && (
              <div className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Due Soon</h3>
                {soonActions.map(a => <ActionCard key={a.id} action={a} />)}
              </div>
            )}
            {waitingActions.length > 0 && (
              <div className="space-y-1.5">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Waiting</h3>
                {waitingActions.map(a => <ActionCard key={a.id} action={a} />)}
              </div>
            )}
            {urgentActions.length === 0 && soonActions.length === 0 && waitingActions.length === 0 && (
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <CheckSquare className="w-6 h-6 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">You're all caught up! No outstanding actions.</p>
              </div>
            )}

            {/* Events */}
            {data && data.events.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" /> Upcoming Events</h2>
                <div className="space-y-2">
                  {data.events.map(e => (
                    <div key={e.id} className="flex items-center gap-3">
                      <div className="w-10 text-center bg-muted rounded py-1 shrink-0">
                        <p className="text-sm font-bold text-primary leading-none">{e.date ? format(parseISO(e.date), 'd') : '?'}</p>
                        <p className="text-xs text-muted-foreground">{e.date ? format(parseISO(e.date), 'MMM') : ''}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{e.eventTitle}</p>
                        <p className="text-xs text-muted-foreground">{[e.time, e.location, e.guestCount ? `${e.guestCount} guests` : ''].filter(Boolean).join(' · ')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column: shifts */}
          <div className="space-y-4">
            <ShiftsCard shifts={shifts} failed={shiftsFailed} isLinked={isLinked} />
            <EmployeeOfMonthBanner />
          </div>
        </div>

        {/* Employment Snapshot */}
        {data && (
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Skills & Capabilities</h2>
              <Link to="/profile" className="text-xs text-primary hover:underline">View Profile →</Link>
            </div>
            <SkillBadges skills={data.user.skills} />
          </div>
        )}

        {/* Quick Actions */}
        {data && <QuickActions stepsCompleted={data.onboardingProgress.stepsCompleted} totalSteps={data.onboardingProgress.totalSteps} isSubmitted={data.onboardingProgress.isSubmitted} />}

        {/* Training Progress */}
        {data && <TrainingProgressSection totalModules={data.trainingStats.totalModules} completedModules={data.trainingStats.completedModules} inProgressModules={data.trainingStats.inProgressModules} />}
      </div>

      {/* Acknowledgement modal */}
      <Dialog open={!!ackAnnouncement} onOpenChange={open => { if (!open) setAckAnnouncement(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{ackAnnouncement?.title}</DialogTitle></DialogHeader>
          {ackAnnouncement && (
            <div className="space-y-4 mt-2">
              <div className="bg-muted rounded-lg p-4 text-sm text-foreground whitespace-pre-wrap max-h-60 overflow-y-auto">{ackAnnouncement.body || 'No message body.'}</div>
              <p className="text-xs text-muted-foreground">Posted by {ackAnnouncement.postedByName}</p>
              {ackAnnouncement.acknowledgementType === 'Tick to Confirm' && (
                <div className="flex items-center gap-2">
                  <Checkbox id="ack-check" checked={ackChecked} onCheckedChange={v => setAckChecked(!!v)} />
                  <label htmlFor="ack-check" className="text-sm text-foreground cursor-pointer">I have read and understood this announcement</label>
                </div>
              )}
              {ackAnnouncement.acknowledgementType === 'Type Initials' && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Type your initials to confirm</label>
                  <Input value={ackInitials} onChange={e => setAckInitials(e.target.value.toUpperCase())} placeholder="e.g. JD" maxLength={5} className="max-w-24" />
                </div>
              )}
              <Button onClick={handleAck} disabled={ackSaving || (ackAnnouncement.acknowledgementType === 'Tick to Confirm' && !ackChecked) || (ackAnnouncement.acknowledgementType === 'Type Initials' && !ackInitials.trim())} className="bg-primary text-primary-foreground w-full">
                {ackSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : ackAnnouncement.acknowledgementType === 'None' ? 'Mark as Read' : <><CheckSquare className="w-4 h-4 mr-2" /> Acknowledge</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
